import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png'
import { createStaffResolution, getStaffResolutions, updateStaffResolution } from "../../services/AppService";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import AddStaffResolutionForm from "../../components/dashboard/Prceedings/Resolutions/AddStaffResolutionForm";
import ViewResolutionDetailsModal from "../../components/dashboard/Prceedings/Resolutions/ViewResolutionDetailsModal";
import { useTranslation } from "react-i18next";


const Resolutions = () => {
    const {t}=useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [resolutions, setResolutions] = useState([]);
    const [editingResolution, setEditingResolution] = useState(null);
    const [selectedResolution, setSelectedResolution] = useState(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState("");
    const [viewModalOpen, setViewModalOpen] = useState(false);



    const [loading, setLoading] = useState(true);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        formData: null,
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: "",
    });

    const showSuccess = (msg) => {
        setMessageModal({
            open: true,
            type: "success",
            title: "Success",
            message: msg,
        })
    }

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Error",
            message: msg,
        })
    }

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({
            open: true,
            formData: formData,
        });
    }


    const handleCancelConfirm = () => {
        setConfirmModal({ open: false, formData: null });
        setIsModalOpen(true);
    };

    useEffect(() => {
        const debouncedSearch = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(debouncedSearch);
    }, [search]);

    useEffect(() => {
        fetchResolutions();
    }, [page, size, debouncedSearch]);

    const fetchResolutions = async () => {
        try {
            setLoading(true);
            const response = await getStaffResolutions(debouncedSearch, page, size);

            if (response) {
                setResolutions(response.content || []);
                setTotalPages(response.totalPages || 0);
            }
        } catch (error) {
            console.error("Error fetching resolutions", error);
            showError("Failed to load resolutions");
        } finally {
            setLoading(false);
        }
    }


    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingResolution) {
                await updateStaffResolution(editingResolution.meetingId || editingResolution.id, formData);
                showSuccess("Resolution updated successfully");
            } else {
                await createStaffResolution(formData);
                showSuccess("Resolution added successfully");
            }
            fetchResolutions();
            setConfirmModal({ open: false, formData: null });
            setEditingResolution(null);
        } catch (error) {
            const backendError = error.response?.data?.message || "Check backend logs";
            showError(backendError);
        }
    };

    const handleAdd = () => {
        setEditingResolution(null);
        setIsModalOpen(true);
    }

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">
                   {t('pageTitles.resolutionsList')}
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={handleAdd}>
                    {t('common.addNew')}
                </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.showing')}:</label>
                    <select
                        value={size}
                        onChange={(e) => {
                            setSize(Number(e.target.value));
                            setPage(0);
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <label className="text-sm">{t('common.entries')}</label>
                </div>
                <div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        className="border rounded px-3 py-1 text-sm"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {loading ? (
                    <p className="text-center p-4">{t('tables.resolutions.loadingResolutions')}</p>
                ) : (
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.meetings.meeting')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                                {/* <th className="px-3 py-2 text-center border-b border-black">Status</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {resolutions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">
                                        {t('tables.resolutions.noResolutions')}
                                    </td>
                                </tr>
                            ) : (
                                resolutions.map((c, idx) => (
                                    <tr
                                        key={c.id}
                                        className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                                            } hover:bg-gray-100`}
                                    >
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2">{c.meetingName}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 py-1 btnImgEdit rounded text-xs"
                                                onClick={() => {
                                                    setEditingResolution(c);
                                                    setIsModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                className="px-3 btnImgView py-1 rounded text-xs"
                                                onClick={() => {
                                                    setSelectedResolution(c);
                                                    setViewModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        {/* <td className="px-3 py-2 text-center">{c.active ? "Active" : "Inactive"}</td> */}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + resolutions.length} {t('common.of')} {" "}
                    {totalPages * size} {t('common.entries')}
                </p>
                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.max(p - 1, 0))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                        disabled={page === totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>

            <AddStaffResolutionForm onSubmit={handleFormSubmit}
                editData={editingResolution}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingResolution(null); }}
            />


            <ConfirmActionModal
                open={confirmModal.open}
                title={editingResolution ? "Confirm Update" : "Confirm Addition"}
                message={
                    editingResolution
                        ? "Are you sure you want to update this resolution details?"
                        : "Are you sure you want to add this new resolution to the system?"
                }
                confirmText={editingResolution ? "UPDATE" : "SAVE"}
                onConfirm={handleConfirmSave}
                onClose={handleCancelConfirm}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
            />

            <ViewResolutionDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                resolution={selectedResolution}
            />

        </div>
    )
}

export default Resolutions;